import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '../../../web/components-v2/Button';
import { Input } from '../../../web/components-v2/Input';
import { IconBell } from '../../focused-view/ui/icons';
import { requestDesktopNotificationPermission } from './desktopNotifications';
import { styleSheet } from './repCards.styles';
import { clampScoreThreshold, type ScoreAlertSettings } from './useScoreAlertSettings';

interface IProps {
	settings: ScoreAlertSettings;
	onChange: (settings: ScoreAlertSettings) => void;
}

export function ScoreAlertControls({ settings, onChange }: IProps) {
	const [draft, setDraft] = React.useState(String(settings.threshold));

	React.useEffect(() => {
		setDraft(String(settings.threshold));
	}, [settings.threshold]);

	// An empty box mid-edit must not read as a threshold of 0, so only a parsable value commits.
	const handleThresholdChange = (raw: string) => {
		setDraft(raw);
		const parsed = Number(raw.trim());
		if (raw.trim() !== '' && Number.isFinite(parsed)) {
			onChange({ ...settings, threshold: clampScoreThreshold(parsed) });
		}
	};

	const handleDesktopToggle = async () => {
		if (settings.desktopEnabled) {
			onChange({ ...settings, desktopEnabled: false });
			return;
		}

		const permission = await requestDesktopNotificationPermission();
		if (permission === 'granted') {
			onChange({ ...settings, desktopEnabled: true });
			return;
		}
		toast.error(
			permission === 'unsupported'
				? 'This browser can’t show desktop notifications.'
				: 'Desktop notifications are blocked for this site. Allow them in your browser settings to be alerted while this tab is in the background.'
		);
	};

	return (
		<div className={css(styleSheet.alertControls)}>
			<span className={css(styleSheet.adminBarLabel)}>Score alerts</span>
			<div className={css(styleSheet.alertControlsRow)}>
				<span className={css(styleSheet.alertText)}>Notify me at</span>
				<Input
					type='number'
					min={0}
					max={100}
					inputMode='numeric'
					aria-label='Score alert threshold'
					value={draft}
					onChange={e => handleThresholdChange(e.target.value)}
					onBlur={() => setDraft(String(settings.threshold))}
					styles={[styleSheet.alertThresholdInput]}
				/>
				<span className={css(styleSheet.alertText)}>and up</span>
				<Button
					variant={settings.desktopEnabled ? 'secondary' : 'ghost'}
					size='sm'
					onClick={handleDesktopToggle}
					aria-pressed={settings.desktopEnabled}
					styles={[styleSheet.alertDesktopButton]}
					title={
						settings.desktopEnabled
							? 'Desktop notifications on — cards keep checking while this tab is in the background'
							: 'Also notify me on the desktop when this tab is in the background'
					}
				>
					<IconBell size={14} />
					Desktop
				</Button>
			</div>
		</div>
	);
}
